import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "videoContainer", "startButton", "stopButton", "status", "result", "error", "controlsContainer", "cameraToggleButton", "validScansCount"]

  connect() {
    this.isScanning = false
    this.stream = null
    this.video = this.videoTarget
    this.worker = null
    this.autoStarted = false
    this.buttonsVisible = false
    this.clickCount = 0
    this.clickTimeout = null
    this.currentFacingMode = 'environment' // Start with back camera

    // Hide controls initially
    this.hideControls()

    // Set initial camera toggle button text
    this.updateCameraToggleButtonText()

    // Load QR code detection library dynamically
    this.loadQRLibrary()

    // Auto-start scanning when component loads
    setTimeout(() => {
      this.startScanning()
    }, 500)
  }

  disconnect() {
    this.stopScanning()
    if (this.worker) {
      this.worker.terminate()
    }
  }

  async loadQRLibrary() {
    try {
      // Using jsQR library for QR code detection
      if (!window.jsQR) {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js'
        script.onload = () => {
          console.log('QR detection library loaded')
        }
        document.head.appendChild(script)
      }
    } catch (error) {
      this.showError('Failed to load QR code detection library')
    }
  }

  async startScanning() {
    if (this.isScanning) return

    try {
      if (!this.autoStarted) {
        this.showStatus('Requesting camera access...')
      }

      // Request camera access with current facing mode
      const constraints = {
        video: {
          facingMode: { ideal: this.currentFacingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      }

      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
      this.video.srcObject = this.stream

      await this.video.play()

      this.isScanning = true
      this.autoStarted = true
      this.updateButtonVisibility()

      this.showScanningMessage()

      // Start the scanning loop
      this.scanForQR()

    } catch (error) {
      console.error('Camera access error:', error)

      let errorMessage = 'Failed to access camera. '
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera access and try again.'
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.'
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Camera access not supported in this browser.'
      } else {
        errorMessage += error.message
      }

      this.showError(errorMessage)
    }
  }

  stopScanning() {
    this.isScanning = false

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    this.video.srcObject = null
    this.updateButtonVisibility()
    this.showStoppedMessage()
  }

  scanForQR() {
    if (!this.isScanning) return

    // Wait for jsQR to be available
    if (!window.jsQR) {
      setTimeout(() => this.scanForQR(), 100)
      return
    }

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    const scan = () => {
      if (!this.isScanning) return

      if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
        canvas.height = this.video.videoHeight
        canvas.width = this.video.videoWidth

        context.drawImage(this.video, 0, 0, canvas.width, canvas.height)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)

        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert"
        })

        if (code) {
          this.handleQRDetected(code.data)
          return
        }
      }

      // Continue scanning
      requestAnimationFrame(scan)
    }

    scan()
  }

  async handleQRDetected(qrData) {
    // Temporarily stop scanning to process the QR code
    this.isScanning = false

    try {
      this.showStatus('Processing QR code...')

      // Send QR data to Rails controller
      const response = await fetch('/qr_scanners', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ qr_data: qrData })
      })

      const result = await this.parseJSONResponse(response)

      if (result.success) {
        if (result.status === 'duplicate') {
          this.showScanWarning('ALREADY SCANNED', result.connect_id)
        } else if (result.status === 'invalid') {
          this.showScanWarning('NOT A RUBYEVENTS PASSPORT', qrData)
        } else {
          this.showThankYou(result.connect_id || qrData)
        }
        this.updateValidScansCount(result.valid_scans_count)

        // Auto-restart scanning after 2 seconds
        setTimeout(() => {
          this.restartScanning()
        }, 2000)
      } else {
        this.showError(result.message, qrData)

        // Restart scanning after error display
        setTimeout(() => {
          this.restartScanning()
        }, 2000)
      }

    } catch (error) {
      console.error('QR processing error:', error)
      this.showError(`Failed to process QR code: ${error.message}`, qrData)

      // Restart scanning after error display
      setTimeout(() => {
        this.restartScanning()
      }, 2000)
    }
  }

  // Safari reports a non-JSON body only as "The string did not match the expected pattern",
  // so parse it ourselves and report what the server actually sent back.
  async parseJSONResponse(response) {
    const body = await response.text()

    try {
      return JSON.parse(body)
    } catch {
      const html = new DOMParser().parseFromString(body, 'text/html')
      const summary = html.title || html.body?.textContent.replace(/\s+/g, ' ').trim().slice(0, 150)
      throw new Error(`Unexpected response from server (HTTP ${response.status}): ${summary || 'empty response'}`)
    }
  }

  updateValidScansCount(count) {
    if (!this.hasValidScansCountTarget || count == null) return

    this.validScansCountTarget.textContent = `(${count} valid ${count === 1 ? 'scan' : 'scans'})`
  }

  showStatus(message) {
    this.hideMessages()

    // Reset to original status styling for regular status messages
    this.statusTarget.className = "absolute top-4 left-4 right-4 bg-black bg-opacity-80 text-white px-4 py-3 rounded-lg text-lg text-center font-semibold hidden"
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove('hidden')
  }

  showResult(message, data) {
    this.hideMessages()
    this.resultTarget.classList.remove('hidden')

    // Display the main message
    const resultContent = this.resultTarget.querySelector('#qr-result-content')
    if (resultContent) {
      let content = `<strong>Type:</strong> ${data.type}<br>`
      content += `<strong>Content:</strong> ${data.content}<br>`
      content += `<strong>Action:</strong> ${data.action}`
      resultContent.innerHTML = content
    }
  }

  showError(message, scannedData = null) {
    this.hideMessages()
    this.errorTarget.classList.remove('hidden')

    const errorContent = this.errorTarget.querySelector('#qr-error-content')
    if (errorContent) {
      errorContent.textContent = message

      if (scannedData) {
        const scanned = document.createElement('div')
        scanned.className = 'mt-1 break-all'
        scanned.textContent = `Scanned: ${scannedData}`
        errorContent.appendChild(scanned)
      }
    }
  }

  hideStatus() {
    this.statusTarget.classList.add('hidden')
  }

  hideMessages() {
    this.statusTarget.classList.add('hidden')
    this.resultTarget.classList.add('hidden')
    this.errorTarget.classList.add('hidden')
  }

  restartScanning() {
    // Resume scanning without stopping the camera stream
    this.isScanning = true
    this.updateButtonVisibility()
    this.showScanningMessage()
    this.scanForQR()
  }

  showScanningMessage() {
    this.hideMessages()

    // Reset to original status styling
    this.statusTarget.className = "absolute top-4 left-4 right-4 bg-black bg-opacity-80 text-white px-4 py-3 rounded-lg text-lg text-center font-semibold hidden"
    this.statusTarget.textContent = 'Scan your QR code'
    this.statusTarget.classList.remove('hidden')
  }

  showThankYou(scannedCode) {
    this.showScanOverlay('bg-green-500 border-green-400', '✓', 'THANKS!', scannedCode)
  }

  // The scan was saved but doesn't add to the valid count
  showScanWarning(title, scannedCode) {
    this.showScanOverlay('bg-amber-500 border-amber-400', '!', title, scannedCode)
  }

  showScanOverlay(colorClasses, icon, title, scannedCode) {
    this.hideMessages()

    // Show the scan result overlaid on the video
    this.statusTarget.classList.remove('hidden')
    this.statusTarget.innerHTML = `
      <div class="${colorClasses} bg-opacity-95 text-white px-8 py-6 rounded-xl shadow-2xl border-4">
        <div class="text-6xl font-black text-center mb-2">${icon}</div>
        <div class="text-3xl font-bold text-center">${title}</div>
        <div class="mt-2 text-lg font-mono text-center break-all" data-scanned-code></div>
      </div>
    `
    // textContent, not innerHTML: the code comes straight from the QR
    this.statusTarget.querySelector('[data-scanned-code]').textContent = (scannedCode || '').slice(0, 60)
    this.statusTarget.className = "absolute inset-0 flex items-center justify-center z-10"
  }

  updateButtonVisibility() {
    // Only show/hide individual buttons if the controls container is visible
    if (this.buttonsVisible) {
      if (this.isScanning) {
        this.startButtonTarget.classList.add('hidden')
        this.stopButtonTarget.classList.remove('hidden')
      } else {
        this.startButtonTarget.classList.remove('hidden')
        this.stopButtonTarget.classList.add('hidden')
      }
    }
  }

  showStoppedMessage() {
    this.hideMessages()

    // Reset to original status styling
    this.statusTarget.className = "absolute top-4 left-4 right-4 bg-black bg-opacity-80 text-white px-4 py-3 rounded-lg text-lg text-center font-semibold hidden"
    this.statusTarget.textContent = 'Camera stopped. Click "Start QR Scanner" to resume.'
    this.statusTarget.classList.remove('hidden')
  }

  handleVideoClick() {
    this.clickCount++

    // Clear any existing timeout
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout)
    }

    // Set timeout to reset click count after 500ms
    this.clickTimeout = setTimeout(() => {
      this.clickCount = 0
    }, 500)

    // Check for double click
    if (this.clickCount === 2) {
      this.clickCount = 0
      clearTimeout(this.clickTimeout)
      this.toggleControls()
    }
  }

  toggleControls() {
    if (this.buttonsVisible) {
      this.hideControls()
    } else {
      this.showControls()
    }
  }

  showControls() {
    this.buttonsVisible = true
    this.controlsContainerTarget.classList.remove('hidden')
    this.updateButtonVisibility()
  }

  hideControls() {
    this.buttonsVisible = false
    this.controlsContainerTarget.classList.add('hidden')
  }

  async toggleCamera() {
    if (!this.isScanning) {
      // If not scanning, just toggle the facing mode and update button text
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user'
      this.updateCameraToggleButtonText()
      return
    }

    try {
      this.showStatus('Switching camera...')

      // Stop current stream
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }

      // Toggle facing mode
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user'

      // Request new camera with updated constraints
      const constraints = {
        video: {
          facingMode: { ideal: this.currentFacingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      }

      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
      this.video.srcObject = this.stream
      await this.video.play()

      // Update button text
      this.updateCameraToggleButtonText()

      // Resume scanning
      this.showScanningMessage()
      this.scanForQR()

    } catch (error) {
      console.error('Camera toggle error:', error)

      // If switching fails, try to revert to previous camera
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user'

      let errorMessage = 'Failed to switch camera. '
      if (error.name === 'NotFoundError') {
        errorMessage += 'The requested camera is not available on this device.'
      } else if (error.name === 'NotAllowedError') {
        errorMessage += 'Camera access was denied.'
      } else {
        errorMessage += error.message
      }

      this.showError(errorMessage)

      // Try to restart with original camera
      setTimeout(() => {
        this.startScanning()
      }, 2000)
    }
  }

  updateCameraToggleButtonText() {
    if (this.hasCameraToggleButtonTarget) {
      const buttonText = this.currentFacingMode === 'user' ? 'Use Back Camera' : 'Use Front Camera'
      const buttonSpan = this.cameraToggleButtonTarget.querySelector('span')
      if (buttonSpan) {
        buttonSpan.innerHTML = `
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          ${buttonText}
        `
      }
    }
  }

  getCSRFToken() {
    const token = document.querySelector('[name="csrf-token"]')
    return token ? token.content : ''
  }
}
